import type { BarIndex } from '../theme/tokens';

/**
 * Stable string-hash for avatar colour selection. djb2-style fold with
 * `Math.imul` so two same-string inputs always map to the same bar.
 */
export function hashName(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Maps a person's name to one of the 8 `palette.bars[N]` slots per the
 * convention documented in `DESIGN-TOKENS.md` line 111:
 *   N = (hash(name) % 8) + 1.
 *
 * Use the result with `theme.palette.bars[N].fill` / `.ink` for the avatar
 * background + foreground.
 */
export function avatarBarIndex(name: string | null | undefined): BarIndex {
  const safe = name && name.length > 0 ? name : 'u';
  return ((hashName(safe) % 8) + 1) as BarIndex;
}

/**
 * Initials helper — first letter of the first and last words, uppercased.
 * Punctuation is not a letter: "Platform (HMT)" reads "PH", not "P(", and a
 * word with no letter or digit in it ("-", "&") is skipped.
 */
export function initials(name: string | null | undefined): string {
  const words = (name ?? '')
    .trim()
    .split(/\s+/)
    .map((word) => word.replace(/^[^\p{L}\p{N}]+/u, ''))
    .filter((word) => word.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]![0]!.toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
